import interact from 'interactjs';

import { IMAGE_CLASSNAME, ZOOM_DATA } from './constants';

const getSlide = (zoom: HTMLElement): HTMLElement => {
  if (!zoom.parentElement || !zoom.parentElement.parentElement) {
    throw new Error('Zoom element is not inside carousel slide!');
  }
  return zoom.parentElement.parentElement;
};

const getImages = (zoom: HTMLElement): HTMLCollectionOf<HTMLImageElement> =>
  getSlide(zoom).getElementsByClassName(
    IMAGE_CLASSNAME,
  ) as HTMLCollectionOf<HTMLImageElement>;

const getPosition = (
  image: HTMLImageElement,
  position: 'left' | 'top',
): number => parseFloat(image.style[position]);

const makePosition = (position: number): string => `${position}px`;

const handleMove = ({
  currentTarget,
  dx,
  dy,
}: {
  currentTarget: HTMLImageElement;
  dx: number;
  dy: number;
}): void => {
  if (!currentTarget.parentElement) {
    throw new Error('Image is not inside carousel slide!');
  }
  const bounding = currentTarget.parentElement.getBoundingClientRect();
  const height = currentTarget.clientHeight;
  const width = currentTarget.clientWidth;
  const maxLeft = width - bounding.width;
  const maxTop = height - bounding.height;
  let left = getPosition(currentTarget, 'left') + dx;
  let top = getPosition(currentTarget, 'top') + dy;
  if (left > 0) left = 0;
  if (top > 0) top = 0;
  if (Math.abs(left) > maxLeft) left = -maxLeft;
  if (Math.abs(top) > maxTop) top = -maxTop;
  currentTarget.style.left = makePosition(left);
  currentTarget.style.top = makePosition(top);
};

const initializeInteract = (image: HTMLImageElement): void => {
  image.style.left = makePosition(0);
  image.style.top = makePosition(0);
  interact(image).draggable({
    onmove: handleMove,
  });
};

const unsetInteract = (image: HTMLImageElement): void =>
  interact(image).unset();

const zoomIn = (event: MouseEvent): void => {
  const currentTarget = event.currentTarget as HTMLElement;
  const [image, imageZoom] = getImages(currentTarget);
  const slide = getSlide(currentTarget);
  currentTarget.setAttribute(ZOOM_DATA, 'true');
  slide.style.height = `${image.clientHeight}px`;
  slide.style.width = `${image.clientWidth}px`;
  image.style.display = 'none';
  imageZoom.style.display = 'block';
  initializeInteract(imageZoom);
};

const zoomOut = (event: MouseEvent): void => {
  const currentTarget = event.currentTarget as HTMLElement;
  const images = getImages(currentTarget);
  currentTarget.setAttribute(ZOOM_DATA, 'false');
  getSlide(currentTarget).removeAttribute('style');
  for (const image of images) {
    image.removeAttribute('style');
    unsetInteract(image);
  }
};

const handleZoom = (event: MouseEvent): void => {
  event.preventDefault();
  const currentTarget = event.currentTarget as HTMLElement;
  if (currentTarget.getAttribute(ZOOM_DATA) === 'true') {
    zoomOut(event);
  } else {
    zoomIn(event);
  }
};

const zoom = {
  init: (elements: NodeListOf<HTMLElement>): void => {
    for (const zoom of elements) {
      zoom.addEventListener('click', handleZoom);
    }
  },
  unset: (elements: NodeListOf<HTMLElement>): void => {
    for (const zoom of elements) {
      zoom.removeEventListener('click', handleZoom);
    }
  },
};

export default zoom;
